import { Injectable, inject, signal, effect, NgZone, isDevMode } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as signalR from '@microsoft/signalr';
import { AuthStateService } from '@dental-ui/auth';
import {
  BASE_API_URL,
  SIGNALR_EVENT_FORCE_LOGOUT,
  SIGNALR_EVENT_GET_ALL_NOTIFICATION,
  SIGNALR_EVENT_SEND_MESSAGE_TO_GROUP,
  SIGNALR_MESSAGE_HUB_PATH,
  SIGNALR_METHOD_JOIN_CONNECTION,
  buildApiUrl,
  buildRequestHeaders,
} from '@dental-ui/shared/consts';
import { Subject } from 'rxjs';

/** api/Log/SendLog gövdesi (WASM log ile aynı şema). */
interface ForceLogoutLogDto {
  project: string;
  from: string;
  level: string;
  message: string;
  errorCode: string;
  errorDetail: string;
}

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection: signalR.HubConnection | null = null;
  private readonly authStateService = inject(AuthStateService);
  private readonly http = inject(HttpClient);
  private readonly ngZone = inject(NgZone);
  private listenersAttached = false;
  private startInFlight = false;
  /** Negotiate 401 sonrası tek seferlik forceRefresh (sonsuz döngüyü önler) */
  private negotiateAuthRetryUsed = false;
  /** communicationId için /Me hydrate — tekrar tekrar denemeyi sınırla */
  private communicationIdHydrateAttempted = false;
  /** Son başarılı JoinConnection id — tekrar tekrar invoke etmeyi önler */
  private joinedCommunicationId: string | null = null;

  // GetAllNotificationAsync için Observable
  private readonly getAllNotificationAsyncSubject = new Subject<unknown>();
  public readonly getAllNotificationAsyncReceived$ = this.getAllNotificationAsyncSubject.asObservable();

  // SendMessageToGroup için Observable
  private readonly sendMessageToGroupSubject = new Subject<string | undefined>();
  public readonly sendMessageToGroupReceived$ = this.sendMessageToGroupSubject.asObservable();

  // ForceLogout için Observable (isteğe bağlı UI bildirimi)
  private readonly forceLogoutSubject = new Subject<void>();
  public readonly forceLogoutReceived$ = this.forceLogoutSubject.asObservable();

  /**
   * Aktif SignalR bağlantı ID'sini döner
   */
  public getConnectionId(): string | null {
    return this.hubConnection?.connectionId || null;
  }

  // Bağlantı durumu sinyali (signal)
  public isConnected = signal(false);

  constructor() {
    effect(() => {
      const user = this.authStateService.user();
      const token = this.authStateService.token();
      const communicationId = user?.communicationId?.trim() || '';

      if (user?.userId && token) {
        this.startConnection();
        // /Me veya login communicationId'yi sonra doldurursa, bağlı hub'a join et
        if (communicationId) {
          this.communicationIdHydrateAttempted = false;
          queueMicrotask(() => this.joinConnection());
        } else {
          // ForceLogout grup olayı communicationId ile gelir — eksikse /Me dene
          queueMicrotask(() => this.ensureCommunicationIdForForceLogout());
        }
      } else {
        this.negotiateAuthRetryUsed = false;
        this.communicationIdHydrateAttempted = false;
        this.stopConnection();
      }
    });
  }

  /**
   * URL handoff / eksik storage sonrası communicationId yoksa Auth/Me ile doldurur.
   * JoinConnection olmadan ForceLogout dialog'u diğer uygulamalarda görünmez.
   */
  private ensureCommunicationIdForForceLogout(): void {
    if (this.communicationIdHydrateAttempted || this.authStateService.hasCommunicationId()) {
      return;
    }

    this.communicationIdHydrateAttempted = true;
    console.warn(
      '[SignalR] communicationId eksik — Auth/Me hydrate deneniyor (ForceLogout için)'
    );

    this.authStateService.hydrateSessionViaMe().subscribe({
      next: (ok) => {
        if (!ok || !this.authStateService.hasCommunicationId()) {
          console.warn(
            '[SignalR] communicationId hâlâ yok — ForceLogout bu oturumda alınamayabilir'
          );
          this.communicationIdHydrateAttempted = false;
        }
      },
      error: () => {
        this.communicationIdHydrateAttempted = false;
      },
    });
  }

  /**
   * Bildirim hub'ı (messagehub) için SignalR bağlantısını başlatır.
   */
  public startConnection(): void {
    const state = this.hubConnection?.state;
    if (
      state === signalR.HubConnectionState.Connected ||
      state === signalR.HubConnectionState.Connecting ||
      state === signalR.HubConnectionState.Reconnecting ||
      this.startInFlight
    ) {
      return;
    }

    const token = this.authStateService.token();
    if (!token) {
      return;
    }

    // Eski Failed/Disconnected instance varsa bırakıp yenisini kur
    if (this.hubConnection) {
      const stale = this.hubConnection;
      this.hubConnection = null;
      this.listenersAttached = false;
      this.joinedCommunicationId = null;
      void stale.stop().catch(() => undefined);
    }

    const hubUrl = `${BASE_API_URL}${SIGNALR_MESSAGE_HUB_PATH}`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        accessTokenFactory: () => this.authStateService.token() || '',
      })
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Information)
      .build();

    this.listenersAttached = false;
    this.addListeners();

    this.hubConnection.onreconnecting(() => {
      console.log('[SignalR] Yeniden bağlanıyor...');
      this.isConnected.set(false);
      this.joinedCommunicationId = null;
    });

    this.hubConnection.onreconnected((connectionId) => {
      this.isConnected.set(true);
      this.joinedCommunicationId = null;
      this.logConnectionEstablished('yeniden-bağlandı', hubUrl, connectionId);
      this.joinConnection();
    });

    this.hubConnection.onclose(() => {
      console.log('[SignalR] Bağlantı kapandı');
      this.isConnected.set(false);
      this.joinedCommunicationId = null;
    });

    this.startInFlight = true;
    this.hubConnection
      .start()
      .then(() => {
        this.negotiateAuthRetryUsed = false;
        this.isConnected.set(true);
        this.logConnectionEstablished('bağlandı', hubUrl, this.hubConnection?.connectionId);
        this.joinConnection();
      })
      .catch((err) => {
        // SignalR hataları oturumu kapatmaz — yalnızca loglanır / bir kez refresh denenir
        const msg = String((err as { message?: string })?.message || err || '');
        console.error('[SignalR] Bağlantı başarısız (oturum etkilenmez):', err);
        if (msg.includes('401')) {
          this.handleNegotiateUnauthorized();
        }
        this.isConnected.set(false);
      })
      .finally(() => {
        this.startInFlight = false;
      });
  }

  /**
   * Hub negotiate 401: Angular interceptor devreye girmez.
   * Bir kez forceRefresh dener; başarıda token effect startConnection'ı tekrar çağırır.
   */
  private handleNegotiateUnauthorized(): void {
    if (this.negotiateAuthRetryUsed) {
      console.warn(
        '[SignalR] Anlaşma 401 — yenileme zaten denendi; tekrar yok (çıkış yapılmadı)'
      );
      return;
    }

    this.negotiateAuthRetryUsed = true;
    console.warn(
      '[SignalR] Anlaşma 401 — hub token reddetti; zorla yenileme deneniyor (çıkış yok)'
    );

    this.authStateService.forceRefreshToken().subscribe({
      next: (token) => {
        if (token) {
          console.info(
            '[SignalR] Zorla yenileme başarılı — yeni token ile hub yeniden bağlanacak'
          );
          // Token signal değişince effect startConnection çağırır; yine de explicit dene
          queueMicrotask(() => this.startConnection());
        } else {
          console.warn(
            '[SignalR] Zorla yenileme geçici başarısız — hub kapalı, oturum SignalR yüzünden kapanmaz'
          );
        }
      },
      error: (err) => {
        console.warn('[SignalR] Zorla yenileme hatası — çıkış yok', err);
      },
    });
  }

  /** Bağlantı kurulduğunda / yeniden kurulduğunda konsola yazar. */
  private logConnectionEstablished(
    kind: 'bağlandı' | 'yeniden-bağlandı',
    hubUrl: string,
    connectionId?: string | null
  ): void {
    const userId = this.authStateService.user()?.userId ?? '-';
    const communicationId = this.authStateService.user()?.communicationId || '-';
    console.log(`[SignalR] Bağlantı ${kind}`, {
      hubUrl,
      connectionId: connectionId ?? this.hubConnection?.connectionId ?? null,
      userId,
      communicationId,
    });
  }

  /**
   * Bağlantıyı durdurur (çıkış işlemleri için kullanışlıdır)
   */
  public stopConnection(): void {
    if (!this.hubConnection) {
      return;
    }

    const connection = this.hubConnection;
    this.hubConnection = null;
    this.listenersAttached = false;
    this.startInFlight = false;
    this.joinedCommunicationId = null;

    connection
      .stop()
      .then(() => {
        console.log('[SignalR] Bağlantı kesildi');
        this.isConnected.set(false);
      })
      .catch((err) => console.error('[SignalR] Bağlantı durdurma hatası:', err));
  }

  /**
   * Kullanıcıyı backend grubuna dahil eder.
   * Hub metodu List/string[] beklediği için communicationId dizi olarak gönderilir.
   */
  private joinConnection(): void {
    const communicationId = this.authStateService.user()?.communicationId?.trim() || '';
    if (!communicationId) {
      console.warn('[SignalR] Gruba katılma atlandı: communicationId eksik');
      return;
    }

    if (
      !this.hubConnection ||
      this.hubConnection.state !== signalR.HubConnectionState.Connected
    ) {
      return;
    }

    if (this.joinedCommunicationId === communicationId) {
      return;
    }

    // Backend: JoinConnection(List<string> / string[]) — tek string değil, dizi gönder
    this.hubConnection
      .invoke(SIGNALR_METHOD_JOIN_CONNECTION, [communicationId])
      .then(() => {
        this.joinedCommunicationId = communicationId;
        console.log('[SignalR] Gruba katılma başarılı', { communicationId });
      })
      .catch((err) => {
        console.error('[SignalR] Gruba katılma hatası:', err);
      });
  }

  /**
   * Sunucudan gelen etkinlikler için dinleyicileri (listener) kaydeder.
   */
  private addListeners(): void {
    if (!this.hubConnection || this.listenersAttached) {
      return;
    }
    this.listenersAttached = true;

    this.hubConnection.on(SIGNALR_EVENT_GET_ALL_NOTIFICATION, (notificationData: unknown) => {
      this.getAllNotificationAsyncSubject.next(notificationData);
    });

    this.hubConnection.on(SIGNALR_EVENT_SEND_MESSAGE_TO_GROUP, (groupName?: string) => {
      console.log('[SignalR] Grup mesajı alındı', groupName);
      this.sendMessageToGroupSubject.next(groupName);
    });

    this.hubConnection.on(SIGNALR_EVENT_FORCE_LOGOUT, () => {
      // Hub callback Zone dışında; dialog CD için Zone içine al
      this.ngZone.run(() => {
        console.warn('[SignalR] Sunucudan zorunlu çıkış olayı alındı');
        // Dev/localhost: oturum kapanmadan önce SendLog (prod'da yok)
        this.reportForceLogoutToLogApi();
        // Yalnızca auth Subject — ForceLogoutDialogService merge'inde forceLogoutReceived$
        // ile birlikte next edilirse dialog açılır açılmaz logout olur.
        this.authStateService.requestForceLogoutDialog();
      });
    });
  }

  /** localhost / ng serve / dev-* hostlarda ForceLogout'u api/Log/SendLog'a yazar. */
  private reportForceLogoutToLogApi(): void {
    if (!this.shouldReportForceLogoutToApi()) {
      return;
    }

    const user = this.authStateService.user();
    const href =
      typeof window !== 'undefined' ? window.location.href : '';
    const detail = {
      source: 'SignalR',
      event: SIGNALR_EVENT_FORCE_LOGOUT,
      at: new Date().toISOString(),
      href,
      userId: user?.userId ?? null,
      email: user?.email ?? null,
      communicationId: user?.communicationId ?? null,
      connectionId: this.hubConnection?.connectionId ?? null,
      hubState: this.hubConnection?.state ?? null,
      joinedCommunicationId: this.joinedCommunicationId,
    };

    const logDto: ForceLogoutLogDto = {
      project: 'DentalUI',
      from: 'SignalR.ForceLogout',
      level: 'warning',
      message: 'SignalR ForceLogout received',
      errorCode: 'ForceLogout',
      errorDetail: JSON.stringify(detail),
    };

    console.warn('[SignalR] ForceLogout → Log/SendLog', detail);

    this.http
      .post(buildApiUrl('log.sendLog'), logDto, {
        headers: buildRequestHeaders(true),
      })
      .subscribe({
        next: () => {
          console.info('[SignalR] ForceLogout log API\'ye yazıldı');
        },
        error: (err) => {
          console.warn('[SignalR] ForceLogout log API başarısız', err);
        },
      });
  }

  private shouldReportForceLogoutToApi(): boolean {
    if (isDevMode()) {
      return true;
    }
    if (typeof window === 'undefined') {
      return false;
    }
    const host = window.location.hostname.toLowerCase();
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host.endsWith('.localhost') ||
      host.startsWith('dev-') ||
      host === 'dev'
    );
  }
}
